import { Label } from "components/custom/programs/fields";
import { useEffect, useMemo, useState } from "react";
import { Badge, Col, Form, Nav, Row } from "react-bootstrap";
import { Flex } from "../../../../styled-system/jsx";
import { Button, LinkButton } from "components/core/basic/Button";
import CenteredModal from "components/core/basic/CenteredModal";
import useDebounce from "hooks/useDebounce";
import { css } from "../../../../styled-system/css";
import logo from "assets/images/logo/Trellis_Logomark_color.png";
import { MdNavigateNext, MdNavigateBefore, MdFirstPage, MdLastPage } from "react-icons/md";
import { Checkbox } from "../../../components/fields/base/Checkbox";

const DEFAULT_PAGE_SIZE = 15;
const MAX_TARGETS_TO_SHOW = 3;

const DSPTargetSelector = ({
  title = "Geographies",
  setSearchQuery = () => {},
  options = [],
  selectedTargets = [],
  selectedExclusions = [],
  renderTargetMode = true,
  renderExcludeMode = false,
  onAdd = () => {},
  onRemove = () => {},
  onReset = () => {},
  searchResultsLoading = false,
  fieldName,
  selectionMode,
  setSelectionMode,
  additionalTabs = [],
  hideSearchField = false,
  isNestedSelector = false,
  customEmptyResultsMessage = null,
  overrideSelector = false,
  customLabelFormatter = null,
  pageSize = DEFAULT_PAGE_SIZE,
  staticData = false,
}) => {
  const [search, setSearch] = useState("");
  const [showSelector, setShowSelector] = useState(false);
  const [page, setPage] = useState(1);

  const debouncedSearchFilter = useDebounce(search, 300);

  useEffect(() => {
    setSearchQuery(debouncedSearchFilter);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchFilter]);

  const pageOptions = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return options?.slice(start, end);
  }, [page, options, pageSize]);

  return (
    <Form.Group>
      <Flex gap="large" alignItems={"center"} justifyContent={"space-between"}>
        <Label className={css({ paddingTop: "10px" })}>{title}</Label>
        <div>
          <LinkButton
            onClick={() => {
              if (showSelector) {
                setSearch("");
                setSearchQuery("");
              }
              setShowSelector(!showSelector);
            }}
          >
            {selectedTargets?.length ? "Modify" : "Add Targets"}
          </LinkButton>
        </div>
      </Flex>
      <Flex direction="column" className="mb-4">
        <p className={css({ fontSize: "14px" })}>
          {selectedTargets?.length} Selected Target{selectedTargets?.length === 1 ? "" : "s"}
        </p>

        {selectedTargets?.slice(0, MAX_TARGETS_TO_SHOW)?.map((target, i) => {
          return (
            <Checkbox
              key={`mainFormTargets-${target.value}-${i}`}
              value={true}
              label={target.label}
              onChange={() => {
                onRemove(target.value, "target");
              }}
            />
          );
        })}
        {selectedTargets.length > MAX_TARGETS_TO_SHOW ?
          <small className={css({ paddingStart: "36px" })}>+ {selectedTargets.length - 3} more</small>
        : null}
        {selectedExclusions?.length ?
          <>
            <p className={css({ fontSize: "14px" })}>
              {selectedExclusions?.length} Selected Exclusion{selectedExclusions?.length === 1 ? "" : "s"}
            </p>
            {selectedExclusions?.slice(0, MAX_TARGETS_TO_SHOW)?.map((exclusion, i) => (
              <Checkbox
                key={`mainFormExclusions-${exclusion.value}-${i}`}
                value={true}
                label={exclusion.label}
                onChange={() => {
                  onRemove(exclusion.value, "exclusion");
                }}
              />
            ))}
            {selectedExclusions.length > MAX_TARGETS_TO_SHOW ?
              <small className={css({ paddingStart: "36px" })}>+ {selectedTargets.length - 3} more</small>
            : null}
          </>
        : null}
      </Flex>
      <Flex direction={"column"}>
        <CenteredModal
          show={showSelector}
          closeModal={() => setShowSelector(false)}
          title={`Update Line Item ${title}`}
          adPlanModal={true}
        >
          <div className="dsp-target-selector">
            <Row className={css({ visibility: hideSearchField ? "hidden" : "inherit" })}>
              <Col xs={12} lg={{ span: 8, offset: 2 }}>
                <Form.Control
                  placeholder={"Search..."}
                  aria-label="Search Term"
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  value={search}
                  style={{ width: "90%" }}
                  className="mt-3 mb-5  mx-auto"
                />
              </Col>
            </Row>

            <Row className="py-3 px-5" style={{ margin: "0 50px" }}>
              <Col xs={12} lg={8}>
                <div
                  style={{ overflowY: "auto", height: "580px" }}
                  className="pe-5 custom-scroll-container category-target-search-scroll-container"
                >
                  <Flex gap="large">
                    <Nav variant="underline" activeKey={selectionMode} onSelect={(mode) => setSelectionMode(mode)}>
                      {renderTargetMode ?
                        <Nav.Item key={"target"}>
                          <Nav.Link eventKey={"target"}>Target</Nav.Link>
                        </Nav.Item>
                      : null}
                      {renderExcludeMode ?
                        <Nav.Item key={"exclude"}>
                          <Nav.Link eventKey={"exclude"}>Exclude</Nav.Link>
                        </Nav.Item>
                      : null}
                      {additionalTabs?.map((tab) => (
                        <Nav.Item key={tab.id}>
                          <Nav.Link eventKey={tab.id}>{tab.label}</Nav.Link>
                        </Nav.Item>
                      ))}
                    </Nav>
                  </Flex>
                  {!["target", "exclude"].includes(selectionMode) ?
                    <>{additionalTabs?.find((tab) => tab.id === selectionMode)?.content ?? <p>Placeholder</p>}</>
                  : searchResultsLoading ?
                    <Flex justifyContent={"center"} alignItems={"center"} className="w-100 h-75">
                      <img src={logo} alt="" className="rotate" width="80" height="auto" />
                    </Flex>
                  : !searchResultsLoading && !search && !isNestedSelector && !staticData ?
                    <Flex alignItems={"center"} className="p-5">
                      <p className="w-100 py-5 text-center fs-standard">
                        {customEmptyResultsMessage ?
                          customEmptyResultsMessage
                        : `Enter a search term above to select ${selectionMode === "target" ? "targets" : "exclusions"}`
                        }
                      </p>
                    </Flex>
                  : <Flex direction={"column"} className="pt-0 overflow-auto">
                      {options?.length ?
                        <Flex alignItems="center" justifyContent={"flex-end"}>
                          <button
                            style={{
                              background: "#fff",
                              color: "#000",
                            }}
                            className="btn-round m-0"
                            onClick={() => {
                              setPage(1);
                            }}
                          >
                            <MdFirstPage />
                          </button>
                          <button
                            style={{
                              background: "#fff",
                              color: "#000",
                            }}
                            className="btn-round m-0"
                            onClick={() => {
                              if (page > 1) {
                                setPage(page - 1);
                              }
                            }}
                          >
                            <MdNavigateBefore />
                          </button>
                          <span className="fs-5">
                            {page} / {Math.ceil(options?.length / pageSize)}
                          </span>
                          <button
                            style={{
                              background: "#fff",
                              color: "#000",
                            }}
                            className="btn-round m-0"
                            onClick={() => {
                              if (page < options?.length / pageSize) {
                                setPage(page + 1);
                              }
                            }}
                          >
                            <MdNavigateNext />
                          </button>
                          <button
                            style={{
                              background: "#fff",
                              color: "#000",
                            }}
                            className="btn-round m-0"
                            onClick={() => {
                              setPage(Math.ceil(options?.length / pageSize));
                            }}
                          >
                            <MdLastPage />
                          </button>
                        </Flex>
                      : null}
                      {overrideSelector ?
                        overrideSelector
                      : pageOptions?.map((option) => {
                          const { label, value } = option;

                          return (
                            <Checkbox
                              key={`${fieldName}-${value}`}
                              value={
                                selectionMode === "exclude" ?
                                  selectedExclusions?.map((exclusion) => exclusion.value).includes(value)
                                : selectedTargets?.map((target) => target.value).includes(value)
                              }
                              label={customLabelFormatter ? customLabelFormatter(option) : label}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  onAdd(value);
                                } else {
                                  onRemove(value);
                                }
                              }}
                            />
                          );
                        })
                      }
                    </Flex>
                  }
                </div>
              </Col>
              <Col
                xs={12}
                lg={4}
                className={`border-start ps-5  ${css({ wordBreak: "break-word", overflowY: "auto", height: "600px" })}`}
              >
                <h4 className="fs-4 my-0 fw-bold d-flex align-items-center gap-2">
                  Selected Targets{" "}
                  <Badge className={`badge badge-secondary`} pill>
                    {selectedTargets?.length}
                  </Badge>
                </h4>

                {selectedTargets?.map((option) => {
                  const { label, value } = option;

                  return (
                    <Checkbox
                      key={`${fieldName}-${value}-selectedTargetsPanel`}
                      value={true}
                      label={label}
                      onChange={() => {
                        onRemove(value, "target");
                      }}
                    />
                  );
                })}
                {renderExcludeMode ?
                  <>
                    <h4 className="fs-4 my-0 fw-bold d-flex align-items-center gap-2">
                      Selected Exclusions{" "}
                      <Badge className={`badge badge-secondary`} pill>
                        {selectedExclusions?.length}
                      </Badge>
                    </h4>
                    {selectedExclusions?.map((option) => {
                      const { label, value } = option;

                      return (
                        <Checkbox
                          key={`${fieldName}-${value}-selectedExclusionsPanel`}
                          value={true}
                          label={label}
                          onChange={() => {
                            onRemove(value, "exclusion");
                          }}
                        />
                      );
                    })}
                  </>
                : null}
              </Col>
            </Row>
            <Flex justifyContent={"center"} gap={"large"} className={"pb-5"}>
              <Button
                variant="secondary"
                onPress={() => {
                  setSearch("");
                  setSearchQuery("");
                  setShowSelector(false);
                  onReset();
                }}
              >
                Cancel
              </Button>
              <Button
                onPress={() => {
                  // console.log("save updated targets...");
                  setSearch("");
                  setSearchQuery("");
                  setShowSelector(false);
                }}
              >
                Confirm
              </Button>
            </Flex>
          </div>
        </CenteredModal>
      </Flex>
    </Form.Group>
  );
};

export default DSPTargetSelector;
